import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useForm, useWatch, Mode, FieldErrors } from "react-hook-form";
import { isArray, isEmpty, mergeWith } from "lodash-es";

import { copy } from "shared/assets";
import { Button, Loader, APIErrorMessage, Message } from "shared/components";
import { Spacer } from "shared/layout";
import {
  IFormSchema,
  ITheme,
  IFormButton,
  IUnitType,
  IAPIState,
  IAPIError,
  IStatus,
} from "shared/types";
import {
  FormFieldContainer,
  FormFieldContainerProps,
  FormContainer,
  FormContainerProps,
  FormButtonContainer,
} from "./styles";
import { getFormItem } from "./utils";
import { ReCaptchaService } from "Venues/services";
import { MutedText } from "shared/styles";
import { ErrorMessage } from "../Fields/styles";

interface FormBuilderProps
  extends IFormSchema,
    FormContainerProps,
    FormFieldContainerProps {
  neverGoBackToDefaultValues?: boolean;
  unitType?: IUnitType;
  isSubmitting?: boolean;
  submitMode?: "isValid" | "isDirty";
  mode?: Mode;
  reValidateMode?: Exclude<Mode, "onTouched" | "all">;
  preventSubmit?: boolean;
  resetForm?: boolean;
  resetFormOnSubmit?: boolean;
  forceUpdateValues?: { [key: string]: any };
  globalError?: boolean | string;
  globalNote?: string;
  globalInfo?: string;
  apiErrorMessage?: boolean | string | JSX.Element;
  apiError?: IAPIError | null;
  withCaptcha?: boolean;
  triggerSubmit?: boolean;
  onSubmit: (data: any) => void;
  onBlur?: (data: any) => void;
  onError?: (errors: FieldErrors) => void;
  children?: ReactNode;
  dataTestId?: string;
  autofillValues?: Record<string, any>;
  renderAs?: React.ElementType;
  showSaveChanges?: boolean;
}

export const FormBuilder = forwardRef<HTMLFormElement, FormBuilderProps>(
  (
    {
      fields,
      defaultValues,
      neverGoBackToDefaultValues,
      buttons,
      unitType,
      isSubmitting,
      submitMode,
      mode = "onSubmit",
      reValidateMode = "onChange",
      preventSubmit,
      inlineForm,
      resetForm,
      resetFormOnSubmit = true,
      forceUpdateValues,
      globalError,
      withCaptcha,
      onSubmit,
      onBlur,
      onError,
      children,
      triggerSubmit,
      apiError,
      apiErrorMessage,
      globalNote,
      globalInfo,
      dataTestId,
      renderAs,
      showSaveChanges,
      ...rest
    },
    ref
  ) => {
    const {
      register,
      handleSubmit,
      errors,
      reset,
      formState,
      control,
      trigger,
      setValue,
      getValues,
    } = useForm({
      defaultValues,
      mode,
      reValidateMode,
    });
    const [token, setToken] = useState<string>();
    const reCaptchaRef = useRef<ReCaptchaService>();
    const [tokenState, setTokenState] = useState<IAPIState>(IAPIState.idle);
    const watchAllFields = useWatch({ control });

    const { isDirty } = formState;
    // prevent warnings
    const { isValid }: any = submitMode === "isValid" ? formState : {};

    const isFormValid = useMemo(() => {
      if (preventSubmit) return false;
      switch (submitMode) {
        case "isValid":
          return isValid;
        case "isDirty":
          return isDirty;
        default:
          return true;
      }
    }, [submitMode, isValid, isDirty, preventSubmit]);

    useEffect(() => {
      if (resetForm) {
        reset(defaultValues);
      }
    }, [resetForm, defaultValues, reset]);

    useEffect(() => {
      if (!forceUpdateValues || isEmpty(forceUpdateValues)) {
        return;
      }

      Object.keys(forceUpdateValues).forEach((name) => {
        setValue(name, forceUpdateValues[name]);
      });
    }, [forceUpdateValues, setValue]);

    // sets default values if they come in asynchronously
    useEffect(() => {
      if (neverGoBackToDefaultValues) return;

      if (defaultValues) {
        reset(defaultValues);
      }
    }, [defaultValues, reset, neverGoBackToDefaultValues]);

    const onFormSubmit = useCallback(
      (data: any) => {
        onSubmit(data);

        if (!resetFormOnSubmit) return;

        reset(
          mergeWith({}, defaultValues, !resetForm && data, (_, srcValue) => {
            // Do not merge arrays
            if (isArray(srcValue)) {
              return srcValue;
            }
          }),
          {
            touched: false,
            isValid: true,
          }
        );
      },
      [defaultValues, onSubmit, reset, resetForm, resetFormOnSubmit]
    );

    const getToken = useCallback(
      (token: string) => {
        setToken(token);
        const data = { ...getValues(), token };
        onFormSubmit(data);
        setTokenState(IAPIState.succeeded);
      },
      [onFormSubmit, getValues]
    );

    useEffect(() => {
      if (withCaptcha && !reCaptchaRef.current) {
        reCaptchaRef.current = new ReCaptchaService(
          getToken,
          () => setTokenState(IAPIState.failed),
          "captcha-container"
        );
      }
    }, [getToken, withCaptcha]);

    const submitForm = useCallback(
      (data: any) => {
        if (withCaptcha) {
          if (!token) {
            setTokenState(IAPIState.pending);
            reCaptchaRef.current?.execute();
          } else {
            onFormSubmit({ ...data, token });
          }
        } else {
          onFormSubmit(data);
        }
      },
      [withCaptcha, token, onFormSubmit]
    );

    // trigger submit externally
    useEffect(() => {
      if (triggerSubmit) {
        submitForm(getValues());
      }
    }, [triggerSubmit, getValues, submitForm]);

    const renderButtonChildren = (
      button: IFormButton | undefined | boolean,
      defaultLabel: string
    ) => {
      if (typeof button !== "boolean" && button?.children) {
        if (typeof button.children === "string") {
          return <span>{button.children}</span>;
        }
        return React.cloneElement(button.children, { isActive: isFormValid });
      }
      return <span>{defaultLabel}</span>;
    };

    const renderFormButtonContainer = (isOutsideForm?: boolean) => {
      const { primary, secondary } = buttons || {};

      if (!primary && !secondary) return null;

      return (
        <FormButtonContainer hasMargin={!inlineForm && !isOutsideForm}>
          {primary !== false && (
            <Button
              type={isOutsideForm ? "button" : "submit"}
              size="l"
              onClick={handleSubmit(submitForm)}
              disabled={(!isSubmitting && !isFormValid) || isSubmitting}
              {...(primary as any)}
            >
              {renderButtonChildren(primary, copy.form.save)}
              {(isSubmitting || tokenState === IAPIState.pending) && (
                <Loader
                  size="s"
                  theme={
                    typeof primary !== "boolean" ? primary?.theme : undefined
                  }
                />
              )}
            </Button>
          )}
          {!!secondary && (
            <Button
              type="button"
              size="l"
              {...(buttons?.secondary as any)}
              onClick={() => {
                if (typeof secondary !== "boolean") {
                  if (secondary?.isSubmitButton) {
                    handleSubmit(submitForm)();
                  }

                  if (secondary?.onClick !== undefined) {
                    secondary?.onClick();
                  } else {
                    reset(defaultValues);
                  }
                }
              }}
              theme={ITheme.secondary}
            >
              {renderButtonChildren(secondary, copy.form.cancel)}
            </Button>
          )}
        </FormButtonContainer>
      );
    };

    const renderApiError = () => {
      if (apiErrorMessage) {
        return (
          <APIErrorMessage
            apiErrorMessage={apiErrorMessage}
            apiError={apiError}
          />
        );
      }

      // captcha error
      if (tokenState === IAPIState.failed) {
        return <APIErrorMessage apiErrorMessage={true} />;
      }
    };

    const handleBlur = () => {
      onBlur && onBlur(watchAllFields);
    };

    useEffect(() => {
      onError && onError(errors);
    }, [errors, onError]);

    const setFieldValue = (field: string, value: string | string[] | null) => {
      setValue(field, value, {
        shouldDirty: true,
      });
    };

    return (
      <React.Fragment>
        <Spacer size="l">
          <Spacer size="s">
            <FormContainer
              ref={ref}
              inlineForm={inlineForm}
              onSubmit={handleSubmit(submitForm)}
              data-testid={dataTestId}
              as={renderAs}
            >
              <Spacer size="m">
                <FormFieldContainer {...rest} onBlur={handleBlur}>
                  {fields.map((item) =>
                    getFormItem(
                      item,
                      control,
                      register,
                      errors,
                      watchAllFields,
                      trigger,
                      unitType,
                      setFieldValue
                    )
                  )}
                </FormFieldContainer>
                {globalNote && <MutedText>{globalNote}</MutedText>}
                {globalInfo && (
                  <Message status={IStatus.info}>{globalInfo}</Message>
                )}
                {showSaveChanges && formState.isDirty && (
                  <Message status={IStatus.warning}>
                    {copy.form.saveChanges}
                  </Message>
                )}
              </Spacer>
              {!children && renderFormButtonContainer()}
              {withCaptcha && <div id="captcha-container" />}
            </FormContainer>
            {globalError && !isEmpty(errors) && (
              <ErrorMessage>
                {typeof globalError === "boolean"
                  ? copy.errors.form
                  : globalError}
              </ErrorMessage>
            )}
            {!children && renderApiError()}
          </Spacer>
          {children}
        </Spacer>
        {children && (
          <Spacer size="s">
            {renderFormButtonContainer(true)}
            {renderApiError()}
          </Spacer>
        )}
      </React.Fragment>
    );
  }
);
